import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './DashboardPage.css';
import { Dashboard } from './common';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Select2Dropdown from './common/Select2Dropdown';
import JobForm from './documentation/JobForm';
import PopoutWindow from './common/PopoutWindow';
import { getAuthHeaders } from '../auth';
import { usePermissions } from '../services/PermissionsContext';

import CustomerList from './masters/CustomerList';
import ShippingLineList from './masters/ShippingLineList';
import SupplierList from './masters/SupplierList';
import TransporterList from './masters/TransporterList';
import VendorList from './masters/VendorList';
import VesselVoyageList from './masters/VesselVoyageList';
import JobList from './documentation/JobList';
import CustomerPortal from './documentation/CustomerPortal';
import TransporterPortal from './port/TransporterPortal';
import SearchItem from './documentation/SearchItem';
import EmailConfigurations from './admin/EmailConfigurations';
import ManifestReport from './reports/ManifestReport';
import PPADReport from './reports/PPADReport';
import AcceptedReport from './reports/AcceptedReport';
import IncompleteJobsReport from './reports/IncompleteJobsReport';
import ShippingReport from './reports/ShippingReport';
import EIRReport from './reports/EIRReport';
import PortReport from './reports/PortReport';
import AirportReport from './reports/AirportReport';
import ExBondReport from './reports/ExBondReport';
import BorderReport from './reports/BorderReport';
import ZNZReport from './reports/ZNZReport';
import ClientReports from './reports/ClientReports';
import PermitReport from './reports/PermitReport';
import Departments from './admin/Departments';
import Staffs from './admin/Staffs';
import Banks from './admin/Banks';
import BankAccounts from './accounts/BankAccounts';
import Payables from './accounts/Payables';
import Receivables from './accounts/Receivables';
import AccountsSettings from './accounts/AccountsSettings';
import FundRequest from './accounts/FundRequest';
import Invoicing from './accounts/Invoicing';
import ClientStatements from './accounts/ClientStatements';
import TransporterInvoicingAdmin from './admin/TransporterInvoicingAdmin';
import DropOffLocations from './masters/DropOffLocations';
import PortConfiguration from './masters/PortConfiguration';
import Permits from './admin/Permits';
import TMDAApplications from './permits/TMDAApplications';
import PermitFunds from './permits/PermitFunds';
import UserManagement from './admin/UserManagement';
import Payroll from './admin/Payroll';
import Attendance from './admin/Attendance';
import Loans from './admin/Loans';
import Advances from './admin/Advances';
import ShippingLinePayables from './admin/ShippingLinePayablesList';
import OtherPayablesList from './admin/OtherPayablesList';
import PayableChargesList from './admin/PayableChargesList';
import ListsConfiguration from './admin/ListsConfiguration';
import UserLogs from './admin/UserLogs';
import Notifications from './admin/Notifications';
import NotificationBell from './common/NotificationBell';
import MissingDocuments from './tools/MissingDocuments';
import QuickReference from './tools/QuickReference';
import ShippingTasksReport from './reports/ShippingTasksReport';
import CompanyDetails from './admin/CompanyDetails';
// import AccountsConfig from './admin/AccountsConfig';

// Map slug -> component
const PermitPayablesRoute = () => <OtherPayablesList module="PERMIT" />;
const PortPayablesRoute = () => <OtherPayablesList module="PORT" />;
const DocPayablesRoute = () => <OtherPayablesList module="DOCUMENTATION" />;
const CashOfficeApprovalsRoute = () => <Payables officeMode={true} pageTitle="Cash/Office Approvals" />;
const AccountsPayablesRoute = () => <OtherPayablesList module="ACCOUNTS" hideNewPayable={true} />;
const componentMap = {
    Dashboard: Dashboard,
    CustomerList: CustomerList,
    ShippingLineList: ShippingLineList,
    SupplierList: SupplierList,
    TransporterList: TransporterList,
    VendorList: VendorList,
    Departments: Departments,
    Staffs: Staffs,
    VesselVoyageList: VesselVoyageList,
    JobList: JobList,
    CustomerPortal: CustomerPortal,
    TransporterPortal: TransporterPortal,
    SearchItem: SearchItem,
    EmailConfigurations: EmailConfigurations,
    ManifestReport: ManifestReport,
    PPADReport: PPADReport,
    AcceptedReport: AcceptedReport,
    IncompleteJobsReport: IncompleteJobsReport,
    ShippingReport: ShippingReport,
    EIRReport: EIRReport,
    PortReport: PortReport,
    AirportReport: AirportReport,
    ExBondReport: ExBondReport,
    BorderReport: BorderReport,
    ZNZReport: ZNZReport,
    ClientReports: ClientReports,
    Banks: Banks,
    BankAccounts: BankAccounts,
    Payables: AccountsPayablesRoute,
    PayablesApprovals: () => <Payables excludeOEPrefix={true} />,
    CashOfficeApprovals: CashOfficeApprovalsRoute,
    DropOffLocations: DropOffLocations,
    PortConfiguration: PortConfiguration,
    Permits: Permits,
    TMDAApplications: TMDAApplications,
    PermitFunds: PermitFunds,
    PermitReport: PermitReport,
    UserManagement: UserManagement,
    Payroll: Payroll,
    Attendance: Attendance,
    Loans: Loans,
    Advances: Advances,
    ShippingLinePayables: ShippingLinePayables,
    PermitPayables: PermitPayablesRoute,
    PortPayables: PortPayablesRoute,
    DocPayables: DocPayablesRoute,
    PayableChargesList: PayableChargesList,
    ListsConfiguration: ListsConfiguration,
    Notifications: Notifications,
    Receivables: Receivables,
    AccountsSettings: AccountsSettings,
    FundRequest: FundRequest,
    Invoicing: Invoicing,
    ClientStatements: ClientStatements,
    TransporterInvoicingAdmin: TransporterInvoicingAdmin,
    UserLogs: UserLogs,
    MissingDocuments: MissingDocuments,
    QuickReference: QuickReference,
    ShippingTasksReport: ShippingTasksReport,
    CompanyDetails: CompanyDetails,
};

const iconMap = {
    "Dashboard": "fa-solid fa-gauge",
    "Documentation": "fa-solid fa-file-lines", 
    "Shipping Line": "fa-solid fa-ship",
    "Port": "fa-solid fa-anchor",
    "Masters": "fa-solid fa-database",
    "Admin Panel": "fa-solid fa-gear",
    "Notifications": "fa-solid fa-bell",
    "User Logs": "fa-solid fa-clipboard-list",
    "Accounts": "fa-solid fa-file-invoice-dollar",
    "Bank Accounts": "fa-solid fa-building-columns",
    "Payables": "fa-solid fa-file-invoice",
    "Payables Approvals": "fa-solid fa-file-invoice-dollar",
    "Cash/Office Approvals": "fa-solid fa-cash-register",
    "Receivables": "fa-solid fa-hand-holding-dollar",
    "Jobs": "fa-solid fa-briefcase",
    "Customer Portal": "fa-solid fa-gear",
    "Transporter Portal": "fa-solid fa-gear",
    "Search Item": "fa-solid fa-magnifying-glass",
    "Shipments": "fa-solid fa-boxes-stacked",
    "View Invoices": "fa-solid fa-file-invoice-dollar",
    "View Documents": "fa-solid fa-file-lines",
    "View Cargo Release Report": "fa-solid fa-file-export",
    "Pending Permits": "fa-solid fa-clock",
    "Customers": "fa-solid fa-user",
    "Suppliers": "fa-solid fa-truck-field",
    "Vendors": "fa-solid fa-store",
    "Shipping Lines": "fa-solid fa-ship",
    "Transporters": "fa-solid fa-truck-moving",
    "Vessel Voyages": "fa-solid fa-ship",
    "Company Configuration": "fa-solid fa-building",
    "Document Templates": "fa-solid fa-file-signature",
    "Email Configurations": "fa-solid fa-envelope-open-text",
    "Departments": "fa-solid fa-sitemap",
    "Staffs": "fa-solid fa-users",
    "Users": "fa-solid fa-user-gear",
    "User Management": "fa-solid fa-user-gear",
    "Roles": "fa-solid fa-user-shield",
    "Expense Categories": "fa-solid fa-list-alt",
    "Reports": "fa-solid fa-chart-pie",
    "Manifest Report": "fa-solid fa-file-alt",
    "P-PAD Report": "fa-solid fa-file-contract",
    "Accepted Assessments": "fa-solid fa-file-circle-check",
    "Incomplete Jobs": "fa-solid fa-clock",
    "Shipping Reports": "fa-solid fa-shipping-fast",
    "EIR Reports": "fa-solid fa-exchange-alt",
    "Port Reports": "fa-solid fa-anchor",
    "Airport Report": "fa-solid fa-plane-arrival",
    "Ex Bond Report": "fa-solid fa-warehouse",
    "Border Report": "fa-solid fa-road",
    "ZNZ Report": "fa-solid fa-anchor",
    "Client Reports": "fa-solid fa-user-group",
    "Banks": "fa-solid fa-bank",
    "Drop Off Locations": "fa-solid fa-map-marker-alt",
    "Port Configurations": "fa-solid fa-cogs",
    "Permits": "fa-solid fa-id-badge", // <-- Added Permits top-level menu icon
    "Permits List": "fa-solid fa-id-card",
    "Permit Reports": "fa-solid fa-file-invoice",
    "TMDA Applications": "fa-solid fa-file-medical",
    "Permit Funds": "fa-solid fa-coins",
    "HR": "fa-solid fa-user-tie", // <-- Added HR Icon
    "Permit Management": "fa-solid fa-id-card",
    "Payroll": "fa-solid fa-money-check-dollar",
    "Attendance": "fa-solid fa-calendar-check",
    "Loans": "fa-solid fa-hand-holding-dollar",
    "Advances": "fa-solid fa-sack-dollar",
    "Accounts Config": "fa-solid fa-sliders",
    "Payables Permissions": "fa-solid fa-user-lock",
    "Payable Charges": "fa-solid fa-tags",
    "Lists Configuration": "fa-solid fa-list-check",
    "Settings": "fa-solid fa-sliders",
    "Invoicing": "fa-solid fa-file-invoice-dollar",
    "Client Statements": "fa-solid fa-file-invoice",
    "Fund Request": "fa-solid fa-money-bill-transfer",
    "Transporter Invoicing": "fa-solid fa-truck-fast",
    "Shipping Tasks Report": "fa-solid fa-clipboard-check",
    "Company Details": "fa-solid fa-building",
    "Tools": "fa-solid fa-screwdriver-wrench",
    "Missing Documents": "fa-solid fa-file-circle-question",
    "Quick Reference": "fa-solid fa-book-open"
};

// The component now receives `menuData` and doesn't need to calculate it.
const NOON_REMINDER_KEY = 'kk_noon_reminder';
const NOON_REPORT_CONFIGS = [
    { key: 'PORT',    label: 'PORT REPORT',    permGroup: 'PORT REPORTS'    },
    { key: 'AIRPORT', label: 'AIRPORT REPORT', permGroup: 'AIRPORT REPORT' },
    { key: 'BORDER',  label: 'BORDER REPORT',  permGroup: 'BORDER REPORT'  },
];

const DashboardPage = ({
    username,
    firstName,
    lastName,
    onLogout,
    menuData,
    hasPermission,
    isSuperAdmin = false,
    maintenanceStatus = null,
    maintenanceBusy = false,
    onStartMaintenance,
    onStopMaintenance,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const applicableReports = NOON_REPORT_CONFIGS.filter(r =>
        hasPermission && (hasPermission(r.permGroup, 'can_create') || hasPermission(r.permGroup, 'can_edit'))
    );

    const [showNoonReminder, setShowNoonReminder] = useState(() => {
        if (!applicableReports.length) return false;
        try {
            const today = new Date().toISOString().split('T')[0];
            const stored = JSON.parse(localStorage.getItem(NOON_REMINDER_KEY) || '{}');
            if (stored.dismissedDate === today) return false;
            if (stored.snoozedUntil && Date.now() < stored.snoozedUntil) return false;
            return true;
        } catch { return true; }
    });

    useEffect(() => {
        if (!applicableReports.length) return;
        const checkSnooze = setInterval(() => {
            try {
                const stored = JSON.parse(localStorage.getItem(NOON_REMINDER_KEY) || '{}');
                if (stored.snoozedUntil && Date.now() >= stored.snoozedUntil) {
                    localStorage.setItem(NOON_REMINDER_KEY, JSON.stringify({ ...stored, snoozedUntil: null }));
                    setShowNoonReminder(true);
                }
            } catch {}
        }, 60000);
        return () => clearInterval(checkSnooze);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicableReports.length]);

    const handleDismissReminder = () => {
        const today = new Date().toISOString().split('T')[0];
        try { localStorage.setItem(NOON_REMINDER_KEY, JSON.stringify({ dismissedDate: today })); } catch {}
        setShowNoonReminder(false);
    };

    const handleSnoozeReminder = () => {
        const snoozedUntil = Date.now() + 60 * 60 * 1000;
        try { localStorage.setItem(NOON_REMINDER_KEY, JSON.stringify({ snoozedUntil })); } catch {}
        setShowNoonReminder(false);
    };

    const noonReminderMessage = (() => {
        if (!applicableReports.length) return '';
        const labels = applicableReports.map(r => r.label);
        if (labels.length === 1) return `PLEASE ENSURE TO GENERATE ${labels[0]} BY NOON`;
        const last = labels[labels.length - 1];
        const rest = labels.slice(0, -1).join(', ');
        return `PLEASE ENSURE TO GENERATE ${rest}, AND ${last} BY NOON`;
    })();
    // Removed redundant state and effects for menu generation
    // const { hasPermission: checkPermission } = usePermissions();
    // const [menuItems, setMenuItems] = useState([]);
    //
    // useEffect(() => {
    //     if (checkPermission) {
    //         const filteredMenu = getMenuConfig(checkPermission);
    //         setMenuItems(filteredMenu);
    //     }
    // }, [checkPermission]);

    // --- FIX #2: Add a useEffect to save the current path on navigation ---
    useEffect(() => {
        // Save the current path to localStorage whenever it changes
        // This ensures that on the next login, we can redirect back here
        if (location.pathname && location.pathname !== '/dashboard') {
            localStorage.setItem('kk_last_route', location.pathname);
        }
    }, [location.pathname]);

    // slug = portion after /dashboard/, strip leading slash
    const rawSlug = location.pathname.startsWith('/dashboard')
        ? location.pathname.replace(/^\/dashboard\/?/, '')
        : '';
    const slug = rawSlug === '' ? '' : decodeURIComponent(rawSlug);

    // If no slug, optionally use persisted last view
    useEffect(() => {
        if (slug === '') {
            const lastView = localStorage.getItem('kk_dashboard_view');
            if (lastView && componentMap[lastView]) {
                navigate(`/dashboard/${lastView}`, { replace: true });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Persist slug (current inner page)
    useEffect(() => {
        if (slug && componentMap[slug]) {
            localStorage.setItem('kk_dashboard_view', slug);
        } else if (slug === '') {
            localStorage.setItem('kk_dashboard_view', 'Dashboard');
        }
    }, [slug]);

    const displayName = firstName && lastName
        ? `${firstName} ${lastName}`
        : username;

    // Derive current component
    const CurrentComponent = useMemo(() => {
        if (!slug) return Dashboard;
        return componentMap[slug] || Dashboard;
    }, [slug]);

    const hasDashboardPermission = isSuperAdmin || (hasPermission && hasPermission('DASHBOARD', 'can_view'));
    const isDashboardRoute = !slug || slug === 'Dashboard';
    const showLogoPage = isDashboardRoute && !hasDashboardPermission;

    // Menu click navigates to /dashboard/<ComponentName>
    const loadPageContent = (link) => {
        if (!link || link === '#' || link === '') return;
        // Existing convention: links like '/react/ManifestReport' OR direct component name
        let compName = link;
        if (link.startsWith('/react/')) {
            compName = link.replace('/react/', '');
        }
        if (compName === 'Dashboard') {
            navigate('/dashboard', { replace: false });
        } else {
            navigate(`/dashboard/${compName}`, { replace: false });
        }
    };

    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [activeSubmenus, setActiveSubmenus] = useState([]);
    const [hoveredMenu, setHoveredMenu] = useState(null);

    // --- Header Job Search ---
    const { permissions } = usePermissions();
    const isAdminDept = permissions?.department_name?.toUpperCase() === 'ADMIN';
    const showHeaderSearch = isSuperAdmin || isAdminDept;
    const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
    const [hdrSearchField, setHdrSearchField] = useState('idf_no');
    const [hdrSearchTerm, setHdrSearchTerm] = useState('');
    const [hdrOptions, setHdrOptions] = useState([]);
    const [hdrLoading, setHdrLoading] = useState(false);
    const [hdrActiveIdx, setHdrActiveIdx] = useState(-1);
    const [hdrJobModal, setHdrJobModal] = useState({ isOpen: false, job: null });
    const hdrSearchRef = useRef(null);

    const HDR_SEARCH_FIELDS = [
        { id: 'idf_no', text: 'IDF NO.' },
        { id: 'bl_no', text: 'BL NO.' },
        { id: 'job_no', text: 'JOB NO.' },
    ];

    const fetchHdrOptions = useCallback(async (term, field) => {
        if (!term || term.length < 2) { setHdrOptions([]); return; }
        setHdrLoading(true);
        try {
            const res = await fetch(
                `/api/documentation/jobs/search_by_idf/?search_field=${encodeURIComponent(field)}&search=${encodeURIComponent(term)}`,
                { headers: getAuthHeaders() }
            );
            if (res.ok) setHdrOptions(await res.json());
        } catch {}
        finally { setHdrLoading(false); }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => fetchHdrOptions(hdrSearchTerm, hdrSearchField), 400);
        return () => clearTimeout(t);
    }, [hdrSearchTerm, hdrSearchField, fetchHdrOptions]);

    const openHdrJob = async (jobId) => {
        try {
            const res = await fetch(`/api/documentation/jobs/${jobId}/`, { headers: getAuthHeaders(false) });
            if (!res.ok) return;
            const data = await res.json();
            setHdrJobModal({ isOpen: true, job: data });
            setHdrOptions([]);
            setHdrSearchTerm('');
        } catch {}
    };

    const getHdrDisplay = (opt) => {
        const parts = [
            opt.idf_no,
            opt.customer_short_name || opt.customer,
            opt.tansad_no,
            opt.bl_no,
        ].filter(Boolean);
        return parts.join(' | ');
    };

    const toggleHeaderSearch = () => {
        setHeaderSearchOpen(prev => {
            if (!prev) setTimeout(() => hdrSearchRef.current?.focus(), 50);
            else { setHdrSearchTerm(''); setHdrOptions([]); }
            return !prev;
        });
    };

    const handleStartMaintenance = () => {
        if (!onStartMaintenance) return;
        const ok = window.confirm('Trigger logout warning for all users? They will be logged out in 30 seconds.');
        if (ok) onStartMaintenance();
    };

    const handleStopMaintenance = () => {
        if (!onStopMaintenance) return;
        const ok = window.confirm('Allow user logins again now?');
        if (ok) onStopMaintenance();
    };

    const handleSubmenuToggle = (idx, event) => {
        event.preventDefault();
        setActiveSubmenus(prev =>
            prev.includes(idx)
                ? prev.filter(i => i !== idx)
                : [...prev, idx]
        );
    };

    const isActive = (itemLinkOrTitle) => {
        let compName = itemLinkOrTitle;
        if (compName.startsWith('/react/')) compName = compName.replace('/react/', '');
        if (!slug && compName === 'Dashboard') return true;
        return slug === compName;
    };

    const renderMenuItems = (items, parentIdx = '', level = 0) => (
        items.map((item, index) => {
            const idx = parentIdx === '' ? index.toString() : `${parentIdx}-${index}`;
            if (item.submenu) {
                return (
                    <li key={idx} className={`menu-item-level-${level}`} style={{ position: 'relative' }}>
                        <div
                            className={`submenu-title ${activeSubmenus.includes(idx) ? 'expanded' : ''}`}
                            onClick={e => {
                                if (sidebarCollapsed) {
                                    setHoveredMenu(hoveredMenu === idx ? null : idx);
                                } else {
                                    handleSubmenuToggle(idx, e);
                                }
                            }}
                        >
                            <span className="icon">
                                <i className={iconMap[item.title] || 'fa-regular fa-folder'}></i>
                            </span>
                            {!sidebarCollapsed && (
                                <>
                                    <span>{item.title}</span>
                                    <i className="fas fa-chevron-down"></i>
                                </>
                            )}
                            {sidebarCollapsed && <div className="menu-tooltip">{item.title}</div>}
                        </div>
                        {!sidebarCollapsed && (
                            <ul className={`submenu level-${level + 1} ${activeSubmenus.includes(idx) ? 'expanded' : ''}`}>
                                {renderMenuItems(item.submenu, idx, level + 1)}
                            </ul>
                        )}
                        {sidebarCollapsed && hoveredMenu === idx && (
                            <ul className="submenu-hover">
                                {item.submenu.map((sub, subIdx) => {
                                    // This part for collapsed sidebar might not fully support 3rd level visually,
                                    // but the main expanded view will work.
                                    const act = isActive(sub.link || sub.title);
                                    return (
                                        <li key={`${idx}-${subIdx}`}>
                                            <a
                                                href="#"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    loadPageContent(sub.link || sub.title);
                                                    setHoveredMenu(null);
                                                }}
                                                className={act ? 'active' : ''}
                                            >
                                                <span className="icon">
                                                    <i className={iconMap[sub.title] || 'fa-regular fa-circle'}></i>
                                                </span>
                                                <span>{sub.title}</span>
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </li>
                );
            }
            const active = isActive(item.link || item.title);
            return (
                <li key={idx} className={`menu-item-level-${level}`}>
                    <a
                        href="#"
                        className={active ? 'active' : ''}
                        onClick={(e) => {
                            e.preventDefault();
                            loadPageContent(item.link || item.title);
                        }}
                    >
                        <span className="icon">
                            <i className={iconMap[item.title] || 'fa-regular fa-circle'}></i>
                        </span>
                        {!sidebarCollapsed && <span>{item.title}</span>}
                        {sidebarCollapsed && <div className="menu-tooltip">{item.title}</div>}
                    </a>
                </li>
            );
        })
    );

    return (
        <>
        <div className="dashboard-page-container">
            <header className="header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button className="mobile-menu-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title="Toggle Menu">
                        <i className={`fa ${sidebarCollapsed ? 'fa-bars' : 'fa-times'}`}></i>
                    </button>
                    <span className="company">K & K Company Ltd.</span>
                    {showHeaderSearch && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
                            <button
                                onClick={toggleHeaderSearch}
                                title={headerSearchOpen ? 'Close job search' : 'Search job'}
                                style={{
                                    background: 'transparent', border: 'none', color: '#fff',
                                    cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1,
                                    fontWeight: 'bold', opacity: 0.85
                                }}
                            >
                                {headerSearchOpen ? '\u2039' : '\u203a'}
                            </button>
                            {headerSearchOpen && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
                                    <div style={{ minWidth: 140 }}>
                                        <Select2Dropdown
                                            options={HDR_SEARCH_FIELDS}
                                            value={hdrSearchField}
                                            onChange={val => { setHdrSearchField(val); setHdrSearchTerm(''); setHdrOptions([]); }}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="Field"
                                            style={{ fontSize: 12 }}
                                        />
                                    </div>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            ref={hdrSearchRef}
                                            type="text"
                                            value={hdrSearchTerm}
                                            onChange={e => { setHdrSearchTerm(e.target.value); setHdrActiveIdx(-1); }}
                                            onKeyDown={e => {
                                                if (e.key === 'ArrowDown') { e.preventDefault(); setHdrActiveIdx(i => Math.min(i + 1, hdrOptions.length - 1)); }
                                                else if (e.key === 'ArrowUp') { e.preventDefault(); setHdrActiveIdx(i => Math.max(i - 1, 0)); }
                                                else if (e.key === 'Enter' && hdrActiveIdx >= 0) { e.preventDefault(); openHdrJob(hdrOptions[hdrActiveIdx].id); setHdrActiveIdx(-1); }
                                                else if (e.key === 'Escape') { setHdrOptions([]); }
                                            }}
                                            placeholder="Search..."
                                            style={{
                                                fontSize: 12, padding: '3px 28px 3px 8px', borderRadius: 4,
                                                border: '1px solid #ccc', width: 180, background: '#fff', color: '#222'
                                            }}
                                        />
                                        <button
                                            onClick={() => fetchHdrOptions(hdrSearchTerm, hdrSearchField)}
                                            style={{
                                                position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
                                                background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', fontSize: 13, padding: '0 4px'
                                            }}
                                            title="Search"
                                        >
                                            <i className="fa fa-search" />
                                        </button>
                                        {hdrOptions.length > 0 && (
                                            <div style={{
                                                position: 'absolute', top: '110%', left: 0,
                                                width: 380, background: '#fff', border: '1px solid #ccc',
                                                borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                                                zIndex: 9999, maxHeight: 240, overflowY: 'auto'
                                            }}>
                                                {hdrLoading && <div style={{ padding: '6px 10px', color: '#888', fontSize: 12 }}>Searching...</div>}
                                                {hdrOptions.map((opt, idx) => (
                                                    <div
                                                        key={opt.id}
                                                        onClick={() => { openHdrJob(opt.id); setHdrActiveIdx(-1); }}
                                                        onMouseEnter={() => setHdrActiveIdx(idx)}
                                                        style={{
                                                            padding: '7px 12px', cursor: 'pointer', fontSize: 12,
                                                            background: idx === hdrActiveIdx ? '#0d6efd' : '#fff',
                                                            color: idx === hdrActiveIdx ? '#fff' : '#212529',
                                                            borderBottom: '1px solid #f0f0f0'
                                                        }}
                                                    >
                                                        {getHdrDisplay(opt)}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {showNoonReminder && applicableReports.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                        <span className="port-reminder-blink">{noonReminderMessage}</span>
                        <button onClick={handleSnoozeReminder} title="Snooze for 1 hour" style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontWeight: 'bold', fontSize: 11, whiteSpace: 'nowrap' }}>Snooze 1h</button>
                        <button onClick={handleDismissReminder} title="Dismiss for today" style={{ background: 'transparent', color: '#fff', border: '1px solid #fff', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13, lineHeight: 1 }}>&times;</button>
                    </div>
                )}
                <div>
                    {isSuperAdmin && (
                        <button
                            onClick={maintenanceStatus?.locked || maintenanceStatus?.warning_active ? handleStopMaintenance : handleStartMaintenance}
                            className="logout-btn"
                            disabled={maintenanceBusy}
                            style={{
                                marginRight: '10px',
                                background: maintenanceStatus?.locked || maintenanceStatus?.warning_active ? '#198754' : '#dc3545',
                                color: '#fff'
                            }}
                            title={maintenanceStatus?.locked || maintenanceStatus?.warning_active ? 'Allow logins again' : 'Log out all users in 30 seconds'}
                        >
                            {maintenanceBusy
                                ? 'Please wait...'
                                : (maintenanceStatus?.locked || maintenanceStatus?.warning_active ? 'Enable Logins' : 'Maintenance Logout')}
                        </button>
                    )}
                    <NotificationBell />
                    <span className="user">User: {displayName}</span>
                    <button onClick={onLogout} className="logout-btn">Logout</button>
                </div>
            </header>
            <div className="main-content-wrapper">
                <nav className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                    <button
                        className="toggle-btn"
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                    >
                        <i className={`fa ${sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
                    </button>
                    <ul className="menu-content">
                        {/* Use the `menuData` prop directly for rendering */}
                        {renderMenuItems(menuData)}
                    </ul>
                </nav>
                <main className={`main-content ${sidebarCollapsed ? 'collapsed' : ''}`}>
                    {showLogoPage ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            padding: '24px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                color: '#333'
                            }}>
                                <span>{firstName || lastName ? `${firstName || ''} ${lastName || ''}`.trim() : ''}</span>
                                <span>{(() => {
                                    const now = new Date();
                                    const day = String(now.getDate()).padStart(2, '0');
                                    const month = String(now.getMonth() + 1).padStart(2, '0');
                                    const year = now.getFullYear();
                                    return `${day}/${month}/${year}`;
                                })()}</span>
                            </div>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '24px',
                                textAlign: 'center'
                            }}>
                                <img
                                    src="/logo.png"
                                    alt="K & K Company Ltd."
                                    style={{ maxWidth: 280, maxHeight: 140, objectFit: 'contain' }}
                                />
                                <h2 style={{ color: '#333', fontWeight: 600, margin: 0 }}>K & K Company Ltd.</h2>
                                <p style={{ color: '#666', margin: 0 }}>Welcome to K & K CFMS</p>
                            </div>
                        </div>
                    ) : (
                        <CurrentComponent />
                    )}
                </main>
            </div>
        </div>

        {hdrJobModal.isOpen && (
            <PopoutWindow
                title={`Job ${hdrJobModal.job?.id || ''} — View Only`}
                name={`job_view_${hdrJobModal.job?.id || Date.now()}`}
                onClose={() => setHdrJobModal({ isOpen: false, job: null })}
            >
                <JobForm
                    mode="view"
                    initialData={hdrJobModal.job}
                    onClose={() => setHdrJobModal({ isOpen: false, job: null })}
                    documentsEditable={false}
                    popoutMode={true}
                />
            </PopoutWindow>
        )}
        </>
    );

};

export default DashboardPage;